// ProgressoCoreSync — componente UNICO de progresso do Monitor de Custos (HUD "CORE SYNC").
// Usado nos 3 gatilhos: "Atualizar precos" (geral), "Buscar" e "Atualizar" de cada familia.
// Mostra HORA:MIN:SEG de INICIO, um RELOGIO ANALOGICO rodando ao vivo, HORA:MIN:SEG de FIM
// (estimado) e a barra de progresso. Flutua centralizado por cima da tela (ver MonitorPrecos).
//
// PROGRESSO HONESTO (ver DA-198): o coletor por codigo NAO reporta sub-progresso. Entao:
//  - geral (varias familias): % REAL = familias concluidas / total -> a barra anda em passos.
//  - 1 familia / nada concluido ainda: barra INDETERMINADA (listras em movimento, sem % falso).
//    A busca por nome reporta pagina X/Y (% real).
//
// EXCECAO de tema proposital: unico ponto do sistema com paleta FIXA (Azul Indigo / escuro, HUD
// sci-fi) — pedido do Ruan. Sai igual no Cobre e no Azul Royal.
import type { CSSProperties, ReactNode } from 'react';

export type EstadoSync = 'progresso' | 'concluido' | 'travado';

// progresso = Azul Indigo; concluido/travado mantem verde/ambar (cores de SIGNIFICADO, nao de tema).
const PALETAS: Record<EstadoSync, { cy: string; dim: string; line: string; soft: string; fill: string; tip: string }> = {
  progresso: { cy: '#818cf8', dim: '#4f46e5', line: 'rgba(129,140,248,.55)', soft: 'rgba(129,140,248,.20)', fill: 'linear-gradient(90deg,#4f46e5 0%,#818cf8 80%,#c7d2fe 100%)', tip: '#e0e7ff' },
  concluido: { cy: '#4dffa8', dim: '#2faa7a', line: 'rgba(77,255,168,.5)',  soft: 'rgba(77,255,168,.18)', fill: 'linear-gradient(90deg,#1fae72 0%,#4dffa8 80%,#d4ffe9 100%)', tip: '#d4ffe9' },
  travado:   { cy: '#ffc44d', dim: '#c98a12', line: 'rgba(255,196,77,.5)',  soft: 'rgba(255,196,77,.18)', fill: 'linear-gradient(90deg,#c98a12 0%,#ffc44d 80%,#fff0c9 100%)', tip: '#fff0c9' },
};

// HH:MM:SS de um timestamp (relogio local). Invalido -> --:--:--
function fmtHMS(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '--:--:--';
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

// DURACAO decorrida em m:ss (ou h:mm:ss se passar de 1h). Negativo/invalido -> 0:00.
function fmtDur(ms: number | null | undefined): string {
  let s = ms == null || !Number.isFinite(ms) || ms < 0 ? 0 : Math.floor(ms / 1000);
  const h = Math.floor(s / 3600); s -= h * 3600;
  const m = Math.floor(s / 60); const ss = s - m * 60;
  const p = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${p(m)}:${p(ss)}` : `${m}:${p(ss)}`;
}

const BEVEL = 14; // chanfro dos cantos da moldura (px)

export function ProgressoCoreSync({
  titulo, alvo, estado = 'progresso', inicioMs, fimEstimadoMs, pct,
  indeterminado = false, rotuloBarra, statusTexto, rodapeEsq, rodapeDir, aviso, onFechar, onCancelar, relogioMs,
}: {
  titulo: string;
  alvo?: string | null;
  estado?: EstadoSync;
  inicioMs: number;
  fimEstimadoMs: number;
  pct: number;
  indeterminado?: boolean;
  rotuloBarra?: ReactNode;
  statusTexto?: string;
  rodapeEsq?: ReactNode;
  rodapeDir?: ReactNode;
  aviso?: ReactNode;
  onFechar?: () => void;       // X = so esconde o HUD (o pedido segue na fila)
  onCancelar?: () => void;     // CANCELAR = aborta a coleta de verdade (zera o pedido pendente)
  relogioMs?: number; // hora atual (ticka a cada seg) — alimenta o relogio analogico rodando
}) {
  const pal = PALETAS[estado];
  const pctClamp = Math.max(0, Math.min(100, Math.round(pct)));
  const bevelClip = `polygon(0 ${BEVEL}px, ${BEVEL}px 0, calc(100% - ${BEVEL}px) 0, 100% ${BEVEL}px, 100% calc(100% - ${BEVEL}px), calc(100% - ${BEVEL}px) 100%, ${BEVEL}px 100%, 0 calc(100% - ${BEVEL}px))`;

  const clock: CSSProperties = {
    fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', fontWeight: 700,
    fontSize: 28, lineHeight: 1, letterSpacing: '.03em',
  };
  const rotulo: CSSProperties = { fontSize: 9.5, letterSpacing: '.14em', color: '#9aa0c4' };

  return (
    <div style={{
      position: 'relative', width: '100%', padding: '12px 16px 12px', color: pal.cy,
      background: 'linear-gradient(180deg, rgba(26,25,58,.94), rgba(10,10,30,.97))',
      clipPath: bevelClip,
      boxShadow: `0 0 0 1px ${pal.line}, 0 0 22px ${pal.soft}, inset 0 0 38px rgba(30,28,70,.5)`,
      fontFamily: 'var(--font-mono)',
    }}>
      <div style={{ position: 'absolute', inset: 4, pointerEvents: 'none', clipPath: bevelClip, boxShadow: `inset 0 0 0 1px ${pal.soft}` }} />

      {/* topo: titulo [ALVO] + STATUS + (x) */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, position: 'relative', zIndex: 2, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <IconeEstado estado={estado} cor={pal.cy} />
          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 15, letterSpacing: '.03em', textShadow: `0 0 8px ${pal.line}` }}>
            {titulo}
            {alvo ? <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 400, color: '#b6bdf2', fontSize: 11.5, marginLeft: 6 }}>[{alvo}]</span> : null}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            fontSize: 10, letterSpacing: '.05em', color: pal.cy, padding: '4px 9px', whiteSpace: 'nowrap',
            border: `1px solid ${pal.line}`, background: pal.soft,
            clipPath: 'polygon(6px 0,100% 0,100% calc(100% - 6px),calc(100% - 6px) 100%,0 100%,0 6px)',
          }}>
            <span style={{ color: '#9aa0c4' }}>STATUS:</span> {statusTexto ?? 'EM ANDAMENTO'}
          </div>
          {onCancelar && (
            <button type="button" onClick={onCancelar} aria-label="Cancelar coleta" title="Cancelar a coleta (zera o pedido pendente)"
              style={{
                display: 'flex', alignItems: 'center', gap: 5, height: 22, padding: '0 9px',
                fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '.08em', fontWeight: 700,
                border: '1px solid rgba(255,196,77,.55)', background: 'rgba(255,196,77,.14)', color: '#ffd98a', cursor: 'pointer',
                clipPath: 'polygon(5px 0,100% 0,100% calc(100% - 5px),calc(100% - 5px) 100%,0 100%,0 5px)',
              }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
              CANCELAR
            </button>
          )}
          {onFechar && (
            <button type="button" onClick={onFechar} aria-label="Fechar" title="Fechar"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22,
                border: `1px solid ${pal.line}`, background: 'rgba(10,10,30,.6)', color: pal.cy, cursor: 'pointer',
                clipPath: 'polygon(5px 0,100% 0,100% calc(100% - 5px),calc(100% - 5px) 100%,0 100%,0 5px)', padding: 0,
              }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </button>
          )}
        </div>
      </div>

      {/* relogios: INICIO (aceso) · [RELOGIO ANALOGICO rodando] · PREVISAO DE FIM (apagado) */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 9,
        padding: '8px 14px', position: 'relative', zIndex: 2,
        background: 'linear-gradient(180deg, rgba(22,21,54,.6), rgba(10,10,28,.4))', border: `1px solid ${pal.soft}`,
        clipPath: 'polygon(0 0,100% 0,100% calc(100% - 7px),calc(100% - 7px) 100%,0 100%)',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={rotulo}>INICIO</span>
          <span style={{ ...clock, color: pal.cy, textShadow: `0 0 10px ${pal.line}` }}>{fmtHMS(inicioMs)}</span>
        </div>
        {/* CENTRO: cronometro DECORRIDO (m:ss) contando ao vivo — quanto tempo ja se passou. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
          <span style={{ ...rotulo, color: pal.cy }}>DECORRIDO</span>
          <span style={{ ...clock, fontSize: 30, color: pal.cy, textShadow: `0 0 14px ${pal.line}` }}>
            {fmtDur(relogioMs != null ? relogioMs - inicioMs : 0)}
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-end' }}>
          <span style={{ ...rotulo, color: '#7177a0' }}>PREVISAO DE FIM · estimativa</span>
          <span style={{ ...clock, color: pal.dim, textShadow: '0 0 8px rgba(79,70,229,.5)' }}>{fmtHMS(fimEstimadoMs)}</span>
        </div>
      </div>

      {/* barra: % real (passos) OU indeterminada (listras em movimento, sem numero falso) */}
      <div style={{ marginTop: 9, position: 'relative', zIndex: 2 }}>
        <div style={{
          position: 'relative', height: 26, border: `1px solid ${pal.line}`, background: 'rgba(8,8,24,.8)',
          clipPath: 'polygon(7px 0,100% 0,calc(100% - 7px) 100%,0 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
        }}>
          {indeterminado ? (
            <>
              {/* base suave + feixe "scanner" que varre de ponta a ponta (vivo, sem % falso) */}
              <div style={{ position: 'absolute', inset: 0, background: pal.fill, opacity: .22 }} />
              <div className="coresync-sweep" style={{
                position: 'absolute', top: 0, bottom: 0, width: '26%',
                background: `linear-gradient(90deg, transparent, ${pal.tip}, ${pal.cy} 55%, transparent)`,
                filter: `drop-shadow(0 0 12px ${pal.line})`, opacity: .95,
              }} />
            </>
          ) : (
            <div style={{
              position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pctClamp}%`,
              background: pal.fill, boxShadow: `0 0 14px ${pal.line}`,
              clipPath: 'polygon(7px 0,100% 0,calc(100% - 7px) 100%,0 100%)', transition: 'width 1s linear',
            }}>
              <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 10, background: `linear-gradient(90deg, transparent, ${pal.tip})`, opacity: .9 }} />
            </div>
          )}
          <span style={{
            position: 'relative', zIndex: 3, fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums',
            fontWeight: 800, fontSize: indeterminado ? 12 : 15, letterSpacing: '.06em',
            color: estado === 'travado' ? '#1a1205' : '#eef0ff', textShadow: '0 0 6px rgba(0,0,0,.55)',
          }}>{indeterminado ? (rotuloBarra ?? 'COLETANDO…') : (rotuloBarra ?? `${pctClamp}%`)}</span>
        </div>
      </div>

      {(rodapeEsq || rodapeDir) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 18, marginTop: 7, position: 'relative', zIndex: 2, fontSize: 10, letterSpacing: '.05em', color: '#9aa0c4' }}>
          <span>{rodapeEsq}</span>
          <span>{rodapeDir}</span>
        </div>
      )}

      {aviso && (
        <div style={{
          marginTop: 8, position: 'relative', zIndex: 2,
          background: '#fde68a', color: '#000', border: '1px solid #d97706', borderLeftWidth: 4,
          borderRadius: 7, padding: '8px 10px', fontSize: 10.5, lineHeight: 1.4,
          fontFamily: 'var(--font-display)', letterSpacing: 0,
        }}>{aviso}</div>
      )}
    </div>
  );
}

function IconeEstado({ estado, cor }: { estado: EstadoSync; cor: string }) {
  const common = { width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none', stroke: cor, strokeWidth: 2.2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, style: { filter: `drop-shadow(0 0 5px ${cor})`, flexShrink: 0 } };
  if (estado === 'concluido') return <svg {...common}><path d="M20 6 9 17l-5-5" /></svg>;
  if (estado === 'travado') return <svg {...common}><path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg>;
  return (
    <svg {...common} className="animate-pulse">
      <path d="M4.9 4.9a10 10 0 0 0 0 14.2M19.1 4.9a10 10 0 0 1 0 14.2M7.8 7.8a6 6 0 0 0 0 8.4M16.2 7.8a6 6 0 0 1 0 8.4" />
      <circle cx="12" cy="12" r="2" fill={cor} stroke="none" />
    </svg>
  );
}
